import { useEffect, useState, useRef } from "react";
import { CalendarDays, Clock } from "lucide-react";
import { getTool } from "@/lib/tools/tool-config";
import { planContingencyTimeline } from "@/lib/tools/scoring";
import { disclaimersFor } from "@/lib/tools/disclaimers";
import { track } from "@/lib/analytics";

import ToolPageShell from "@/components/tools/ToolPageShell";
import {
    FormSection,
    PillMultiSelect,
    PillSingleSelect,
    NumberField,
    DateField,
} from "@/components/tools/ToolFormFields";
import {
    PrimaryCtaBlock,
    DisclaimerList,
    RelatedArticles,
    CostSafeNote,
    ConcernBadge,
} from "@/components/tools/ToolResultShell";
import EmailResultsForm from "@/components/tools/EmailResultsForm";
import SoftPaywallGate from "@/components/tools/SoftPaywallGate";
import useCalcSoftPaywall from "@/components/tools/useCalcSoftPaywall";

const TOOL_SLUG = "inspection-contingency-timeline-planner";
const TOOL = getTool(TOOL_SLUG);

const ISSUES = [
    { value: "roof", label: "Roof" },
    { value: "foundation", label: "Foundation" },
    { value: "water_intrusion", label: "Water intrusion" },
    { value: "electrical", label: "Electrical" },
    { value: "plumbing", label: "Plumbing" },
    { value: "hvac", label: "HVAC" },
    { value: "mold_pest", label: "Mold / pest" },
    { value: "safety", label: "Safety" },
];

const AGENT = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
];

const UNDER_CONTRACT = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
];

const BUYER_WANTS = [
    { value: "repairs", label: "Repairs" },
    { value: "credit", label: "Credit" },
    { value: "price_reduction", label: "Price reduction" },
    { value: "decision_clarity", label: "Decision clarity" },
];

const FAQS = [
    {
        q: "How long is an inspection contingency?",
        a: "Common windows are 7–14 days, though it varies by market and contract. Read your contract for the exact deadline date and any time-of-day cutoff.",
    },
    {
        q: "What should I do before the inspection deadline?",
        a: "Review the report, identify top priorities, gather contractor quotes if possible, align with your agent, and submit a written ask before the deadline.",
    },
    {
        q: "Should I get contractor quotes during inspection period?",
        a: "For the top 1–2 high-impact issues, written quotes add a lot of leverage. They also speed up alignment with your agent and the seller's side.",
    },
    {
        q: "Can I negotiate after inspection contingency ends?",
        a: "It depends on your contract. Once the contingency lapses, your leverage typically narrows. Aim to submit your ask before the deadline.",
    },
];

const INTRO =
    "Map out what to do before the inspection contingency deadline. Calm checklist, urgency-aware ordering, no fear-based copy.";

const URGENCY_LEVEL = {
    critical: "high",
    high: "high",
    moderate: "moderate",
    low: "low",
    unknown: "low",
};

export default function ContingencyTimelinePlanner() {
    const [inspectionDate, setInspectionDate] = useState("");
    const [deadlineDate, setDeadlineDate] = useState("");
    const [issues, setIssues] = useState([]);
    const [contractorQuotesNeeded, setContractorQuotesNeeded] = useState("");
    const [agentInvolved, setAgentInvolved] = useState("yes");
    const [underContract, setUnderContract] = useState("yes");
    const [buyerWants, setBuyerWants] = useState("decision_clarity");
    const [result, setResult] = useState(null);
    const { gated, tryShowResult, unlock } = useCalcSoftPaywall();
    const resultRef = useRef(null);

    useEffect(() => {
        track("tool_started", { tool_name: TOOL_SLUG });
    }, []);

    const onSubmit = (e) => {
        e.preventDefault();
        const out = planContingencyTimeline({
            inspectionDate,
            deadlineDate,
            issues,
            contractorQuotesNeeded: Number(contractorQuotesNeeded) || 0,
            agentInvolved,
            underContract,
            buyerWants,
        });
        if (!tryShowResult(out, setResult)) return;
        track("tool_completed", {
            tool_name: TOOL_SLUG,
            result_level: URGENCY_LEVEL[out.urgency],
            completed: true,
        });
        setTimeout(() => {
            resultRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
            track("tool_result_viewed", {
                tool_name: TOOL_SLUG,
                result_level: URGENCY_LEVEL[out.urgency],
            });
        }, 50);
    };

    const reset = () => {
        setIssues([]);
        setResult(null);
    };

    return (
        <ToolPageShell tool={TOOL} intro={INTRO} faqs={FAQS}>
            {gated && (
                <SoftPaywallGate toolSlug={TOOL_SLUG} onUnlock={unlock} />
            )}
            {!result && !gated && (
                <form
                    onSubmit={onSubmit}
                    className="bl-card p-6 sm:p-8 space-y-7"
                    data-testid="timeline-form"
                >
                    <FormSection title="Your dates">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <DateField
                                label="Inspection date"
                                value={inspectionDate}
                                onChange={setInspectionDate}
                                testid="inspection-date"
                            />
                            <DateField
                                label="Contingency deadline"
                                value={deadlineDate}
                                onChange={setDeadlineDate}
                                testid="deadline-date"
                            />
                        </div>
                    </FormSection>

                    <FormSection
                        title="Major issues found"
                        hint="Tap each issue you may want to address."
                    >
                        <PillMultiSelect
                            options={ISSUES}
                            value={issues}
                            onChange={setIssues}
                            testidPrefix="issue"
                        />
                    </FormSection>

                    <FormSection title="Logistics">
                        <NumberField
                            label="Contractor quotes you'd like to gather"
                            value={contractorQuotesNeeded}
                            onChange={setContractorQuotesNeeded}
                            placeholder="0–3"
                            min={0}
                            max={10}
                            testid="quotes-needed"
                        />
                        <div className="pt-3">
                            <p className="text-sm text-bl-muted mb-2">
                                Agent involved?
                            </p>
                            <PillSingleSelect
                                options={AGENT}
                                value={agentInvolved}
                                onChange={setAgentInvolved}
                                testidPrefix="agent"
                            />
                        </div>
                        <div className="pt-3">
                            <p className="text-sm text-bl-muted mb-2">
                                Under contract?
                            </p>
                            <PillSingleSelect
                                options={UNDER_CONTRACT}
                                value={underContract}
                                onChange={setUnderContract}
                                testidPrefix="under-contract"
                            />
                        </div>
                        <div className="pt-3">
                            <p className="text-sm text-bl-muted mb-2">
                                What you want
                            </p>
                            <PillSingleSelect
                                options={BUYER_WANTS}
                                value={buyerWants}
                                onChange={setBuyerWants}
                                testidPrefix="buyer-wants"
                            />
                        </div>
                    </FormSection>

                    <button
                        type="submit"
                        disabled={!deadlineDate}
                        className="bl-btn-primary w-full sm:w-auto justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="timeline-submit"
                    >
                        <CalendarDays className="h-4 w-4" />
                        Generate my timeline
                    </button>
                </form>
            )}

            {result && (
                <div ref={resultRef} className="space-y-6">
                    <ResultCard result={result} />
                    <PrimaryCtaBlock toolSlug={TOOL_SLUG} includeClosingCta />
                    <CostSafeNote />
                    <EmailResultsForm
                        toolSlug={TOOL_SLUG}
                        resultSummary={{
                            daysLeft: result.daysLeft,
                            urgency: result.urgency,
                            issueCount: issues.length,
                        }}
                    />
                    <DisclaimerList disclaimers={disclaimersFor(TOOL_SLUG)} />
                    <RelatedArticles
                        slugs={TOOL.relatedArticles}
                        toolSlug={TOOL_SLUG}
                    />
                    <div className="text-center">
                        <button
                            type="button"
                            onClick={reset}
                            className="text-sm underline underline-offset-4 text-bl-muted hover:text-bl"
                            data-testid="timeline-reset"
                        >
                            Reset and try different inputs
                        </button>
                    </div>
                </div>
            )}
        </ToolPageShell>
    );
}

function ResultCard({ result }) {
    return (
        <div
            className="bl-card p-6 sm:p-8 space-y-6 reveal-mount"
            data-testid="timeline-result"
        >
            <div className="flex flex-wrap items-center justify-between gap-3">
                <ConcernBadge level={URGENCY_LEVEL[result.urgency]} />
                <div className="flex items-center gap-1.5 text-sm text-bl-muted">
                    <Clock className="h-4 w-4" />
                    {result.daysLeft == null
                        ? "—"
                        : result.daysLeft <= 0
                            ? "Deadline today or passed"
                            : `${result.daysLeft} day${result.daysLeft === 1 ? "" : "s"} remaining`}
                </div>
            </div>

            <h2 className="font-display font-semibold text-2xl sm:text-3xl text-bl tracking-tight">
                {result.urgency === "critical"
                    ? "Move quickly — focused checklist below."
                    : result.urgency === "high"
                        ? "Tight window — prioritize the top 2–3 items."
                        : result.urgency === "moderate"
                            ? "You have room — work the plan in order."
                            : "Comfortable window — plan thoroughly."}
            </h2>

            {result.reminders?.length > 0 && (
                <div className="rounded-lg border border-bl bg-[var(--bl-amber-tint)] p-4 space-y-1">
                    {result.reminders.map((r, i) => (
                        <p
                            key={r}
                            className="text-sm text-[var(--bl-amber)]"
                            data-testid={`reminder-${i}`}
                        >
                            {r}
                        </p>
                    ))}
                </div>
            )}

            <div>
                <h3 className="text-sm font-semibold text-bl mb-3">
                    Your suggested order
                </h3>
                <ol className="space-y-3">
                    {result.steps.map((step) => (
                        <li
                            key={step.order}
                            className="flex gap-3"
                            data-testid={`step-${step.order}`}
                        >
                            <span className="shrink-0 h-7 w-7 rounded-full bg-bl-primary text-white text-xs font-semibold flex items-center justify-center mt-0.5">
                                {step.order}
                            </span>
                            <div>
                                <div className="text-sm font-semibold text-bl">
                                    {step.title}
                                </div>
                                <p className="text-sm text-bl-muted mt-0.5 leading-relaxed">
                                    {step.detail}
                                </p>
                            </div>
                        </li>
                    ))}
                </ol>
            </div>
        </div>
    );
}
